/**
 * appointmentWhatsAppUtils.js
 *
 * Utilitário centralizado para envio de mensagem WhatsApp ao criar um agendamento.
 *
 * Usado por:
 *   - AppointmentDrawer (novo agendamento via drawer)
 *   - Appointments.js (agendamento via lista de espera no SlotPopover)
 *
 * IMPORTANTE: deve ser chamado ANTES de qualquer callback assíncrono (resposta de API),
 * pois window.open só é permitido pelo browser em resposta direta a evento de usuário.
 */

import MessagesTemplate from '../consts/MessagesTemplate';
import { SendWhatsAppMessage } from '../api/WhatsappAPI';

const WEEKDAY_NAMES = [
  'Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira',
  'Quinta-feira', 'Sexta-feira', 'Sábado',
];

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/**
 * Valor da variável {price} das mensagens no formato brasileiro: 180 -> "180,00",
 * "1800.5" -> "1.800,50". Vazio sem valor; texto que não é número segue como veio.
 */
export function formatMessagePrice(price) {
  if (price === null || price === undefined || price === '') return '';
  const value = Number(price);
  if (Number.isNaN(value)) return String(price);
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Preenche um modelo de mensagem com os dados de um agendamento (texto puro,
 * ainda sem a conversão para o formato do WhatsApp). Modelo vazio devolve ''.
 *
 * @param {string} template              - Texto do modelo (com {name}, {day}, ...)
 * @param {Object} params
 * @param {string} params.patientName    - Nome completo do paciente
 * @param {Date}   params.aptDate        - Data do agendamento (objeto Date)
 * @param {string} params.time           - Hora no formato "HH:MM"
 * @param {string} params.doctorName     - Nome do profissional
 * @param {number|string} params.price   - Valor do procedimento
 * @param {Object} params.companyConfig  - CompanyConfig do DataProvider
 * @returns {string}
 */
export function renderAppointmentMessage(template, { patientName, aptDate, time, doctorName, price, companyConfig }) {
  let message = template || '';
  if (!message) return '';

  const dayOfWeek = WEEKDAY_NAMES[aptDate.getDay()];
  const day = String(aptDate.getDate()).padStart(2, '0');
  const monthName = MONTH_NAMES[aptDate.getMonth()];
  const patientFirstName = (patientName || '').split(' ')[0];

  // replaceAll (não replace): variáveis como {company_name} podem aparecer
  // mais de uma vez no mesmo template (ex.: AppointmentCreatedFullMessage cita
  // "{company_name}" na abertura e novamente no aviso de remarcação) — replace()
  // trocaria só a primeira ocorrência e deixaria a segunda literal no texto enviado.
  message = message.replaceAll('{name}',         patientFirstName);
  message = message.replaceAll('{dayOfWeek}',    dayOfWeek);
  message = message.replaceAll('{day}',          day);
  message = message.replaceAll('{month}',        monthName);
  message = message.replaceAll('{time}',         time);
  message = message.replaceAll('{when}',         dayOfWeek);
  message = message.replaceAll('{doctor_name}',  doctorName || '');
  message = message.replaceAll('{company_name}', companyConfig?.Name || '');
  message = message.replaceAll('{address}',      `${companyConfig?.Address || ''}, ${companyConfig?.Number || ''}`);
  message = message.replaceAll('{price}',        formatMessagePrice(price || ''));
  return message;
}

/**
 * Envia a mensagem de confirmação de agendamento via WhatsApp.
 *
 * @param {Object} params
 * @param {string} params.patientPhone   - Telefone do paciente (apenas dígitos)
 * @param {string} params.patientName    - Nome completo do paciente
 * @param {string} params.countryCode    - Código do país (apenas dígitos, ex: "55")
 * @param {Date}   params.aptDate        - Data do agendamento (objeto Date)
 * @param {string} params.time           - Hora no formato "HH:MM"
 * @param {string} params.doctorName     - Nome do profissional
 * @param {number|string} params.price   - Valor do procedimento
 * @param {Object} params.companyConfig  - CompanyConfig do DataProvider
 * @param {boolean} [params.rescheduled] - Usa o modelo de reagendamento (o novo
 *   horário substitui um agendamento que o paciente já tinha)
 */
export function sendAppointmentCreatedWhatsApp({
  patientPhone,
  patientName,
  countryCode,
  aptDate,
  time,
  doctorName,
  price,
  companyConfig,
  rescheduled = false,
}) {
  const phone = (patientPhone || '').replace(/\D/g, '');
  if (!phone) return;

  const templates = MessagesTemplate.getTemplates(companyConfig);
  const template = rescheduled
    ? templates.AppointmentRescheduledConfirmedMessage || ''
    : templates.AppointmentCreatedFullMessage || templates.AppointmentCreatedShortMessage || '';
  const message = renderAppointmentMessage(template, { patientName, aptDate, time, doctorName, price, companyConfig });

  if (message) {
    SendWhatsAppMessage(phone, MessagesTemplate.ConvertToWhatsAppText(message), String(countryCode || '').replace(/\D/g, ''));
  } else {
    // Template não configurado — abre WhatsApp sem mensagem pré-preenchida
    SendWhatsAppMessage(phone, '', String(countryCode || '').replace(/\D/g, ''));
  }
}
